import { request, coerceSecond, queryClient, L2TxReqParams } from '@/utils';
import { useCallback } from 'react';
import { useInfiniteQuery, useMutation, useQuery } from 'react-query';
import { HiMessage, HiMessageLocal, HiSession, HiUserInfo } from '../types';
import { useHiSessionWith } from './hiSession';
import { useCurrentAddressInfo, useOperateHiChatSessionMessages } from 'hooks';

const MESSAGE_PAGE_COUNT = 20;

type HttpMessageListQuery = {
  session_id: HiSession['id'];
  limit?: number;
  direction?: 'next' | 'prev';
  create_at?: number;
};
export function useHiMessageList(
  { session_id, limit, direction = 'next', create_at }: Partial<HttpMessageListQuery>,
  {
    page_key = '',
    other_side_id,
  }: {
    page_key?: string | number;
    other_side_id?: HiUserInfo['id'];
  } = {},
) {
  const { address: meId } = useCurrentAddressInfo();
  const { putMessageOnSession } = useOperateHiChatSessionMessages();

  return useInfiniteQuery(
    ['/hi/message/list', session_id, meId, page_key],
    async ({ pageParam }) => {
      if (!session_id) {
        const result = await request.get<HiSession>('/hi/session', {
          other_user_id: other_side_id!,
        });
        session_id = result.id;
      }

      return await request
        .get<HiMessage[]>('/hi/message/list', {
          session_id,
          other_side_id,
          limit: MESSAGE_PAGE_COUNT,
          ...(direction && { direction }),
          ...(create_at && { create_at }),
          ...(pageParam || {}),
          page_key,
        })
        .then(data => {
          putMessageOnSession(session_id!, data);
          return data;
        });
    },
    {
      staleTime: 0,
      cacheTime: 0,
      // enabled: !!session_id,
      getNextPageParam: (lastPage: HiMessage[]) => {
        const len = lastPage?.length;
        if (!lastPage?.[len - 1]?.create_at) {
          return {
            direction: 'next',
          };
        }

        return {
          create_at: coerceSecond(lastPage![len - 1].create_at),
          direction: 'next',
        };
      },
      getPreviousPageParam: (firstPage: HiMessage[]) => {
        if (!firstPage[0]?.create_at) return;

        const len = firstPage?.length;
        if (len === MESSAGE_PAGE_COUNT) {
          return {
            create_at: firstPage![0].create_at,
            direction: 'prev',
          };
        }
        return;
      },
    },
  );
}

export function useConversationMessageList(
  params: Partial<HttpMessageListQuery> & {
    other_side_id?: string;
    pageKey?: number;
  },
) {
  const { data: sessionInfo, ...sessionQuery } = useHiSessionWith(
    {
      other_side_id: params.other_side_id,
      session_id: params.session_id,
    },
    {
      page_key: params.pageKey,
    },
  );
  const { data: messageListData, ...messagePagesQuery } = useHiMessageList(
    {
      session_id: sessionInfo?.id,
      ...params,
    },
    {
      page_key: params.pageKey,
      other_side_id: params.other_side_id,
    },
  );

  const refetch = useCallback(async () => {
    sessionQuery.refetch();
    messagePagesQuery.refetch();
  }, [sessionQuery, messagePagesQuery]);

  const { mutateAsync: readMessage } = useReadMessage();
  const readMessages = useCallback(
    async (ids: HiMessage['id'][]) => {
      await Promise.allSettled(
        ids.map(id =>
          readMessage({ message_id: id, session_id: sessionInfo?.id || params.session_id }),
        ),
      );
    },
    [params.session_id, readMessage, sessionInfo?.id],
  );

  const messagePages = messageListData?.pages || [];
  return {
    sessionInfo,
    sessionQuery,
    messagePages,
    messagePagesQuery,
    refetch,
    isFetching: sessionQuery.isFetching || messagePagesQuery.isFetching,
    isLoading: sessionQuery.isLoading || messagePagesQuery.isLoading,
    readMessages,
  };
}

export function useAddMessage() {
  const { putMessageOnSession } = useOperateHiChatSessionMessages();

  return useMutation(
    ({
      toUserId,
      data,
      tx,
      bonus,
    }: {
      toUserId: string;
      data: HiMessage['data'];
      offerPrice?: number;
      tx: L2TxReqParams;
      bonus?: number;
    }) => {
      return request
        .post<HiMessage & { session_id: HiSession['id'] }>('/hi/message/add', {
          to_user_id: toUserId,
          type: 'user_text',
          data,
          tx,
          bonus,
        })
        .then(data => {
          putMessageOnSession(data.session_id, [data]);
          return data;
        });
    },
  );
}

function useReadMessage() {
  const { putMessageOnSession } = useOperateHiChatSessionMessages();

  return useMutation(
    ({ message_id, session_id }: { message_id: HiMessage['id']; session_id?: HiSession['id'] }) => {
      return request
        .put<HiMessage & { session_id: HiSession['id'] }>(`/hi/message/read`, {
          id: message_id,
          session_id,
        })
        .then(
          data => {
            putMessageOnSession(data.session_id, [data]);
            return data;
          },
          (err_msg: string) => {
            if (err_msg === 'message has been read') {
              console.log('Hi message:', err_msg);
            }
          },
        );
    },
  );
}
export function useMessageAction(message_id: HiMessageLocal['id']) {
  return useMutation((actionName: 'click_image' | 'click_url') => {
    return request.post(`/hi/receiver_action/create`, {
      name: actionName,
      message_id: String(message_id),
      device_type: 'web',
    });
  });
}

export function usePostMessageTyping() {
  return useMutation((toUserId: string) => {
    return request
      .post(`/hi/message/typing`, {
        to_user_id: toUserId,
      })
      .catch(e => console.warn(e));
  });
}

export const useFetchMessageTyping = (fromUserId?: string) => {
  return useQuery(
    ['/hi/message/typing', fromUserId],
    async () => {
      return await request
        .get<{ is_typing: boolean }>(
          '/hi/message/typing',
          {
            from_user_id: fromUserId!,
          },
          { timeout: 1000 },
        )
        .then(data => Boolean(data.is_typing))
        .catch(e => console.warn(e));
    },
    { enabled: Boolean(fromUserId), refetchInterval: 2000 },
  );
};

export const invalidateMessageTyping = (otherSideHiId: string) => {
  queryClient.setQueriesData(['/hi/message/typing', otherSideHiId], () => false);
};
